import React, {
    createContext,
    Dispatch,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import mixpanel from 'mixpanel-browser';
import { isAxiosError } from 'axios';

import { Button, Headline } from '@freenow/wave';
import Logger from '@mytaxi/logging';
import { ErrorPanel } from '@mytaxi/react-error-screens';

import { trackLoginError } from '../../tracking/helpers/googleTagManager/trackEvents';
import { AuthenticationContext } from '../../authentication/context/AuthenticationContext';
import {
    CentralOrganisation,
    getAvailableCentralOrganisations,
    getCentralOrganisationDetails,
} from '../api/central-organisations.api';
import { CenteredContentWrapper, CenteredLoadingIndicator, ErrorScreen, GreyRelativeWrapper } from '../components';
import { useFormatMessage } from '../hooks/useFormatMessage';
import LOCAL_STORAGE_KEYS from '../constants/common.localStorageKeys';
import { httpStatusCodes } from '../constants/httpStatusCodes';
import { ROUTES } from '../../routing/constants/routes';

export interface ICentralOrganisationContext {
    page: number;
    setPage: Dispatch<SetStateAction<number>>;
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    currentCentralOrganisation?: CentralOrganisation;
    updateCurrentCentralOrganisation: (centralOrganisationId: number, userInitiated?: boolean) => void;
    centralOrganisations?: CentralOrganisation[];
    totalPages?: number;
    totalCentralOrganisations: number;
    searchedTotalCentralOrganisations: number;
    loading: boolean;
    centralOrganisationChanged: boolean;
    setCentralOrganisationChanged: Dispatch<SetStateAction<boolean>>;
}

export const CentralOrganisationContext = createContext<ICentralOrganisationContext>({
    page: 0,
    setPage: () => {
        throw Error('CentralOrganisationContext is not initialized.');
    },
    search: '',
    setSearch: () => {
        throw Error('CentralOrganisationContext is not initialized.');
    },
    currentCentralOrganisation: undefined,
    updateCurrentCentralOrganisation: () => {
        throw Error('CentralOrganisationContext is not initialized.');
    },
    centralOrganisations: undefined,
    totalPages: undefined,
    totalCentralOrganisations: 0,
    searchedTotalCentralOrganisations: 0,
    loading: true,
    centralOrganisationChanged: false,
    setCentralOrganisationChanged: () => {
        throw Error('CentralOrganisationContext is not initialized.');
    },
});

export const CentralOrganisationContextProvider = (props: { children: React.ReactNode }) => {
    const formatMessage = useFormatMessage();
    const history = useHistory();
    const { logout } = useContext(AuthenticationContext);
    const [page, setPage] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentCentralOrganisation, setCurrentCentralOrganisation] = useState<CentralOrganisation | undefined>();
    const [totalCentralOrganisations, setTotalCentralOrganisations] = useState<number>(0);
    const [searchedTotalCentralOrganisations, setSearchedTotalCentralOrganisations] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [centralOrganisations, setCentralOrganisations] = useState<CentralOrganisation[] | undefined>(undefined);
    const [centralOrganisationListError, setCentralOrganisationListError] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [centralOrganisationChanged, setCentralOrganisationChanged] = useState(false);
    const centralOrganisationsRef = useRef<CentralOrganisation[] | undefined>();

    useEffect(() => {
        const fetchAvailableOrganisations = async () => {
            try {
                setLoading(true);
                const response = await getAvailableCentralOrganisations(page, searchQuery);
                centralOrganisationsRef.current = response.data.centralOrganisationList;
                setCentralOrganisations(response.data.centralOrganisationList);
                setSearchedTotalCentralOrganisations(response.data.pageMetadata.totalElements);
                setTotalPages(response.data.pageMetadata.totalPages);

                if (!searchQuery) {
                    if (response.data.pageMetadata.totalElements === 0) {
                        setInitialLoading(false);
                        return;
                    }
                    setTotalCentralOrganisations(response.data.pageMetadata.totalElements);
                }
                if (!currentCentralOrganisation) {
                    const storedCentralOrganisation = localStorage.getItem(
                        LOCAL_STORAGE_KEYS.CURRENT_CENTRAL_ORGANISATION_ID,
                    );

                    if (storedCentralOrganisation) {
                        const parsed = JSON.parse(storedCentralOrganisation);
                        updateCurrentCentralOrganisation(parsed.id || parsed, false);
                    } else {
                        updateCurrentCentralOrganisation(response.data.centralOrganisationList[0].id, false);
                    }
                }
            } catch (error) {
                Logger.error('Failed to fetch available central organisations', { error });
                setCentralOrganisationListError(true);
                setInitialLoading(false);
            } finally {
                setLoading(false);
            }
        };

        fetchAvailableOrganisations();
        // don't want to refetch on currentCentralOrganisation change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, searchQuery]);

    useEffect(() => {
        setPage(0);
    }, [searchQuery]);

    const updateCurrentCentralOrganisation = useCallback(
        async (centralOrganisationId: number, userInitiated: boolean = true) => {
            try {
                const response = await getCentralOrganisationDetails(centralOrganisationId);
                setCurrentCentralOrganisation(response.data);
                mixpanel.register({ CentralOrganisationId: response.data.id });
                if (userInitiated) {
                    setCentralOrganisationChanged(true);
                }
                localStorage.setItem(
                    LOCAL_STORAGE_KEYS.CURRENT_CENTRAL_ORGANISATION_ID,
                    JSON.stringify(centralOrganisationId),
                );
            } catch (error) {
                if (
                    isAxiosError(error) &&
                    (error.response?.status === httpStatusCodes.CLIENT_ERROR.FORBIDDEN ||
                        error.response?.status === httpStatusCodes.CLIENT_ERROR.NOT_FOUND) &&
                    centralOrganisationsRef.current
                ) {
                    updateCurrentCentralOrganisation(centralOrganisationsRef.current[0].id, false);
                    history.push(ROUTES.ROOT);
                } else {
                    setCentralOrganisationListError(true);
                    Logger.error('Failed to fetch central organisation details', { error });
                }
            } finally {
                setInitialLoading(false);
            }
        },
        [history],
    );

    if (initialLoading) {
        return <CenteredLoadingIndicator />;
    }

    if (centralOrganisationListError) {
        return (
            <ErrorPanel
                title={formatMessage('businessAccountContext.failedToLoadBusinessAccounts')}
                subTitle={formatMessage('errorPanel.header.headerSubTitle')}
                detailsTitle=""
                buttonTitle=""
                errorStack=""
                showMoreInformation={false}
            />
        );
    }

    if (!searchQuery && centralOrganisations && totalCentralOrganisations === 0) {
        trackLoginError('Not an Admin');

        return (
            <GreyRelativeWrapper>
                <CenteredContentWrapper>
                    <ErrorScreen>
                        <Headline as="h4" mb={2}>
                            <FormattedMessage id="login.errors.noAdmin" />
                        </Headline>
                        <Button variant="secondary" size="small" onClick={() => logout()}>
                            <FormattedMessage id="businessAccountContext.logout" />
                        </Button>
                    </ErrorScreen>
                </CenteredContentWrapper>
            </GreyRelativeWrapper>
        );
    }

    const context = {
        page,
        setPage,
        search: searchQuery,
        setSearch: setSearchQuery,
        currentCentralOrganisation,
        updateCurrentCentralOrganisation,
        centralOrganisations,
        totalCentralOrganisations,
        totalPages,
        searchedTotalCentralOrganisations,
        loading,
        centralOrganisationChanged,
        setCentralOrganisationChanged,
    };

    return <CentralOrganisationContext.Provider {...props} value={context} />;
};
